import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { PRICING, IS_PRELAUNCH } from "@/lib/pricing";
import { Events } from "@/lib/analytics";
import ReserveDealModal from "./ReserveDealModal";

const DISMISSED_KEY = "sib_promo_banner_dismissed_v1";

/**
 * Persistent but dismissible promo banner for the Home tab.
 * Mobile-optimized, visually distinct but not intrusive.
 */
export default function ProPromoBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    setVisible(!dismissed);
  }, []);

  const handleDismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  const handleCta = () => {
    Events.upgradeClick("yearly_5_99", "home_promo_banner");
    if (IS_PRELAUNCH) {
      setReserveOpen(true);
    } else {
      navigate("/pricing");
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="mx-4 mt-3 rounded-2xl overflow-hidden relative shadow-md cursor-pointer active:scale-[0.99] transition-transform"
        onClick={handleCta}
        data-testid="pro-promo-banner"
        style={{
          background:
            "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 50%, #4FB3A5 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background:
              "radial-gradient(circle at 85% 30%, rgba(255,255,255,0.6), transparent 40%)",
          }}
        />

        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          data-testid="pro-promo-banner-close"
          className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white/80 hover:text-white transition-colors"
        >
          <X size={13} />
        </button>

        <div className="relative p-3.5 pr-8 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-[13px] leading-tight mb-0.5">
              {PRICING.promo.headline}
            </p>
            <p className="text-white/75 text-[11px] leading-snug">
              {IS_PRELAUNCH
                ? "Reserve now · No charge today · Activate at app launch"
                : `${PRICING.promo.freeVsPro} · Limited time only`}
            </p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCta();
            }}
            data-testid="pro-promo-banner-cta"
            className="flex-shrink-0 bg-white text-[#1F3A5F] text-xs font-bold px-3.5 py-2 rounded-xl hover:bg-white/90 active:scale-95 transition-all shadow-sm whitespace-nowrap"
          >
            {IS_PRELAUNCH ? "Reserve" : PRICING.promo.cta}
          </button>
        </div>
      </div>

      <ReserveDealModal
        isOpen={reserveOpen}
        onClose={() => setReserveOpen(false)}
        source="home_promo_banner"
      />
    </>
  );
}
